import GridSpinner from 'components/UI/GridSpinner'
import { Button, Grid, Icon, Tooltip } from 'gu-ui-kit'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import PageHeader from 'components/PageHeader'
import { usePromisedPayment } from 'modules/Finance/PromisedPayment/hooks/usePromisedPayment'
import Table from 'modules/Finance/PromisedPayment/subcomponents/Table'
import EmptyResults from 'components/EmptyResults'
import { Link } from 'react-router-dom'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import useTotallyEmptyList from 'utils/useTotallyEmptyList'
import config from 'modules/Finance/PromisedPayment/config/filters'
import { SET_PROMISED_PAYMENT_FILTERS } from 'modules/Finance/PromisedPayment/store/types'
import FilterPanel from 'components/Filters/FilterPanel'
import clsx from 'clsx'
import styles from 'modules/Finance/PromisedPayment/PromisedPayment.module.scss'
import prepareFilterParams from 'utils/prepareFilterParams'

const PromisedPayment = () => {
  const { t } = useTranslation()
  const pathGen = useGeneratePathI18n()
  const breakpoint = useBreakpoint()

  const [page, setPage] = useState(1)

  const {
    settings = {},
    takenPromisedPayment = {},
    promisedPayment: {
      data = [],
      loaded,
      pagination: { limit = 50 } = {},
    } = {},
    filters,
  } = useSelector((state) => state['finance/promised-payment'])

  const { i18n } = useSelector(({ app }) => app)

  const {
    getSettings,
    getTakenPromisedPayment,
    resetPromisedPayment,
    getPromisedPayment,
    dropPromisedPayment,
  } = usePromisedPayment()

  const filterManager = useFiltersManager({
    filters,
    config: config(),
    setAction: SET_PROMISED_PAYMENT_FILTERS,
  })

  const didMount = useRef(false)
  useEffect(() => {
    if (didMount.current) {
      resetPromisedPayment()
    }
    didMount.current = true
    getSettings()
    return () => {
      resetPromisedPayment()
    }
  }, [i18n])

  useEffect(() => {
    if (!settings.loaded || settings.loading || !settings.available) {
      return
    }
    getTakenPromisedPayment()
  }, [settings])

  useEffect(() => {
    if (!settings?.wallets?.length) {
      return
    }

    filterManager.updateFilterConfigurationByKey({
      key: 'wallets',
      val: {
        options: settings.wallets.map(({ id, financial_profile, name }) => {
          const { custom_name = null, financial_profile_type: { type = null } = {} } =
            financial_profile || {}

          return {
            value: id,
            label: name || custom_name || t(`finance::types.${type}`),
          }
        }),
      },
    })
  }, [settings])

  useEffect(() => {
    if (!filterManager.filters) {
      return
    }
    dropPromisedPayment()
    setPage(1)
  }, [filterManager.filters])

  useEffect(() => {
    if (!filterManager.filters || !settings.available) {
      return
    }

    let { statuses = [], ...rest } = filterManager.filters
    const params = { statuses: [...statuses] }
    const findIndex = statuses.findIndex((value) => value === 'expired')

    if (findIndex > -1) {
      params.is_expired = true
      params.statuses.splice(findIndex, 1)
    }

    getPromisedPayment({
      offset: limit * (page - 1),
      limit,
      ...prepareFilterParams({ ...params, statuses, ...rest }),
    })
  }, [filterManager.filters, page, limit, settings.available])

  const [totallyEmptyList, firstLoad] = useTotallyEmptyList(filterManager.params, data, loaded)

  if (!settings.loaded || (settings.available && !takenPromisedPayment.loaded)) {
    return <GridSpinner />
  }

  if (!settings.available) {
    return (
      <div className={'layout-tile margin-bottom-16'}>
        <EmptyResults
          header={t('promised_payment::not_available.heading')}
          text={t('promised_payment::not_available.summary')}
          actionButton={
            <Button
              size={'l'}
              variant={'primary'}
              component={'a'}
              target={'_blank'}
              grey
              href={pathGen(ROUTES.PROMISED_PAYMENT_TERMS)}
              startIcon={<Icon name={'document'} />}
            >
              {t('common::buttons.terms')}
            </Button>
          }
          icon={
            <Icon
              name={'time-money'}
              width={64}
            />
          }
        />
      </div>
    )
  }

  if (firstLoad) {
    return <GridSpinner />
  }

  if (totallyEmptyList) {
    return (
      <div className={'layout-tile margin-bottom-16'}>
        <EmptyResults
          header={t('promised_payment::totally_empty_data.heading')}
          text={t('promised_payment::totally_empty_data.summary')}
          actionButton={
            <Grid
              justifyContent={'center'}
              gap={16}
            >
              <Button
                size={'l'}
                variant={'primary'}
                component={'a'}
                target={'_blank'}
                grey
                href={pathGen(ROUTES.PROMISED_PAYMENT_TERMS)}
                startIcon={<Icon name={'document'} />}
              >
                {t('common::buttons.terms')}
              </Button>
              <Button
                size={'l'}
                variant={'primary'}
                component={Link}
                to={pathGen(ROUTES.FINANCE_PROMISED_PAYMENT_REQUEST)}
                startIcon={<Icon name={'plus'} />}
              >
                {t('common::buttons.create_request')}
              </Button>
            </Grid>
          }
          icon={
            <Icon
              name={'time-money'}
              color={'Interface-Onsurface'}
              width={64}
            />
          }
        />
      </div>
    )
  }

  const hasTakenPromisedPayment =
    takenPromisedPayment.loaded && takenPromisedPayment?.data?.length > 0

  return (
    <>
      <div className={'layout-tile margin-bottom-16'}>
        <Grid
          alignItems={'center'}
          justifyContent={'start'}
          gap={8}
          className={'margin-bottom-24'}
        >
          <PageHeader>{t('promised_payment::heading')}</PageHeader>
          <Button
            component={'a'}
            target={'_blank'}
            href={pathGen(ROUTES.PROMISED_PAYMENT_TERMS)}
            variant={'ghost'}
            size={'m'}
            noLabel
            startIcon={
              <Icon
                name={'information'}
                color={'Interface-Seventh'}
                size={'m'}
              />
            }
          ></Button>
        </Grid>
        {hasTakenPromisedPayment ? (
          <Tooltip title={t('promised_payment::expired_promised_payment.tooltip')}>
            <Button
              fullWidth={!breakpoint.sm}
              variant={'primary'}
              size={'l'}
              startIcon={<Icon name={'plus'} />}
              disabled
            >
              {t('common::buttons.create_request')}
            </Button>
          </Tooltip>
        ) : (
          <Button
            fullWidth={!breakpoint.sm}
            variant={'primary'}
            size={'l'}
            startIcon={<Icon name={'plus'} />}
            component={Link}
            to={pathGen(ROUTES.FINANCE_PROMISED_PAYMENT_REQUEST)}
          >
            {t('common::buttons.create_request')}
          </Button>
        )}
      </div>
      <div className={'layout-tile'}>
        <FilterPanel
          filterManager={filterManager}
          className={clsx(styles['promised-payment__filter-panel'], 'margin-bottom-32')}
        />
        <Table onShowMore={() => setPage((prev) => prev + 1)} />
      </div>
    </>
  )
}

export default PromisedPayment
